import { CalendarDays, MessageCircle, Ticket, MapPin } from "lucide-react";
import { Reveal } from "./Reveal";
import { waLink, mediaUrl } from "../../lib/site";

const DEFAULT_CONCERT = {
  status: "upcoming",
  date_label: "Coming November 2026",
  title: "A Special Evening of Music with Tricity Kroonerz",
};

export default function Concert({ concert = DEFAULT_CONCERT }) {
  const past = concert.status === "past";
  const hasPoster = Boolean(concert.poster);

  const info = (
    <div className={hasPoster ? "text-left" : "text-center"}>
      <p className="text-xs font-bold uppercase tracking-[0.35em] text-gold">
        {past ? "Past Event" : "Upcoming Concert"}
      </p>
      <h2 className="mt-4 font-serif text-3xl font-bold text-ivory sm:text-4xl lg:text-5xl" data-testid="concert-date-label">
        {past ? `Past Event — ${concert.date_label}` : concert.date_label}
      </h2>
      <p className="mt-4 font-serif text-lg italic text-gold-light sm:text-xl">{concert.title}</p>
      {concert.venue && (
        <p className={`mt-4 flex items-center gap-2 text-sm font-semibold text-slate-300 ${hasPoster ? "" : "justify-center"}`} data-testid="concert-venue">
          <MapPin className="h-4 w-4 shrink-0 text-gold" /> {concert.venue}
        </p>
      )}
      {concert.details && <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-300">{concert.details}</p>}
      <p className="mt-6 text-xs uppercase tracking-[0.2em] text-slate-500">
        An evening of retro Bollywood & English classics · Chandigarh Tricity
      </p>
      <div className={`mt-8 flex flex-wrap items-center gap-3 ${hasPoster ? "" : "justify-center"}`}>
        {past && concert.ticket_link ? (
          <a href={concert.ticket_link} target="_blank" rel="noreferrer" data-testid="concert-tickets-button" className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 text-sm font-bold text-navy-950 hover:bg-gold-light">
            <Ticket className="h-4 w-4" /> Event Details
          </a>
        ) : (
          <span data-testid="concert-coming-soon-badge" className="inline-flex cursor-default items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-7 py-3 text-sm font-bold text-gold">
            <CalendarDays className="h-4 w-4" /> Event Details Coming Soon
          </span>
        )}
        <a
          href={waLink("Hi Tricity Kroonerz, I'd like to know more about your upcoming concert in November 2026.")}
          target="_blank"
          rel="noreferrer"
          data-testid="concert-whatsapp-button"
          className="inline-flex items-center gap-2 rounded-full border border-navy-700 px-6 py-3 text-sm font-semibold text-ivory transition-colors hover:border-gold/50 hover:text-gold"
        >
          <MessageCircle className="h-4 w-4" /> Ask About the Concert
        </a>
      </div>
    </div>
  );

  return (
    <section id="concert" className="relative overflow-hidden py-20 lg:py-28" data-testid="concert-section">
      <div className="spotlight-burgundy pointer-events-none absolute left-1/2 top-1/2 h-[640px] w-[640px] -translate-x-1/2 -translate-y-1/2" />
      <div className={`relative mx-auto px-4 sm:px-6 ${hasPoster ? "max-w-6xl" : "max-w-4xl"}`}>
        <Reveal>
          <div className="gold-frame relative overflow-hidden rounded-3xl border border-gold/30 bg-navy-900/80 px-6 py-14 sm:px-12" data-testid="concert-card">
            {!hasPoster && <div className="vinyl absolute -left-16 -top-16 h-48 w-48 opacity-40" />}
            {!hasPoster && <div className="vinyl absolute -bottom-20 -right-16 h-56 w-56 opacity-30" />}
            {hasPoster ? (
              <div className="relative grid items-center gap-10 md:grid-cols-2">
                <div className="overflow-hidden rounded-2xl border border-gold/25 shadow-2xl">
                  <img src={mediaUrl(concert.poster)} alt="Tricity Kroonerz concert poster" loading="lazy" className="w-full object-cover" data-testid="concert-poster" />
                </div>
                {info}
              </div>
            ) : (
              <div className="relative">{info}</div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
